import React, { useState, MouseEvent } from "react";
import {
    Button,
    CircularProgress,
    Popover,
    Box,
    Typography,
    TextField
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { currencySelector } from "../../../redux/currencyReducer";
import { useModifyPaymentsBalanceMutation } from "../../../api/reports";

interface WithdrawFormProps {
    service: string;
    balances: { [currency: string]: string | number };
}

const WithdrawForm: React.FC<WithdrawFormProps> = ({ service, balances }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    // Состояние формы
    const [amount, setAmount] = useState<string>("");
    const [currency, setCurrency] = useState("");
    const [wallet, setWallet] = useState("");
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const { items: currencyList } = useSelector(currencySelector);
    const [modify, { isLoading }] = useModifyPaymentsBalanceMutation();

    const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setErrorMsg(null);
        setAmount("");
        setWallet("");
        setCurrency("");
    };

    // Фильтруем только те валюты, которые физически есть на балансе
    const availableCurrencies = currencyList.filter(c => {
        const balance = Number(balances[c.id] || 0);
        return balance > 1e-9;
    });

    const currentBalance = Number(balances[currency] || 0);
    const numAmount = Number(amount);

    // Валидация: сумма > 0, кошелек заполнен, валюта выбрана и хватает средств
    const canSubmit =
        numAmount > 0 &&
        currency !== "" &&
        wallet.trim() !== "" &&
        numAmount <= currentBalance &&
        !isLoading;

    const handleWithdraw = async () => {
        setErrorMsg(null);
        try {
            await modify({
                service,
                amount: -numAmount,
                currency,
                wallet // Передаем адрес кошелька в API
            }).unwrap();

            handleClose();
        } catch (err: any) {
            const message = err?.data?.message || err?.error || "Ошибка при выводе средств";
            setErrorMsg(message);
        }
    };

    const isOpen = Boolean(anchorEl);

    return (
        <>
            <Button variant="contained" color="secondary" onClick={handleOpen}>
                Вывести
            </Button>

            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Box p={2} display="flex" flexDirection="column" style={{ width: "280px", gap: "12px" }}>
                    <Typography variant="subtitle2" style={{ marginBottom: '4px' }}>
                        Вывод: {service}
                    </Typography>

                    <select
                        value={currency}
                        onChange={(e) => {
                            setCurrency(e.target.value);
                            setErrorMsg(null);
                        }}
                        style={{ padding: "8px", borderRadius: "4px", border: "1px solid rgba(0,0,0,0.2)" }}
                    >
                        <option value="">Выберите валюту</option>
                        {availableCurrencies.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.id} (Баланс: {balances[c.id]})
                            </option>
                        ))}
                    </select>

                    <TextField
                        label="Сумма"
                        type="number"
                        value={amount}
                        onChange={(e) => {
                            setAmount(e.target.value);
                            setErrorMsg(null);
                        }}
                        fullWidth
                        size="small"
                    />

                    <TextField
                        label="Адрес кошелька"
                        placeholder="Введите адрес"
                        value={wallet}
                        onChange={(e) => {
                            setWallet(e.target.value);
                            setErrorMsg(null);
                        }}
                        fullWidth
                        size="small"
                    />

                    {errorMsg && (
                        <Typography color="error" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                            {errorMsg}
                        </Typography>
                    )}

                    <Box display="flex" justifyContent="space-between" mt={1}>
                        <Button onClick={handleClose} size="small">Отмена</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!canSubmit}
                            onClick={handleWithdraw}
                            size="small"
                        >
                            {isLoading ? <CircularProgress size={20} color="inherit" /> : "Отправить"}
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default WithdrawForm;